import { useState, useCallback } from 'react'
import {
  GameState,
  Decisions,
  INITIAL_STATE,
  GAME_CONSTANTS,
} from '../types/game'

export function useGameState() {
  const [state, setState] = useState<GameState>(INITIAL_STATE)
  const [lastDecisions, setLastDecisions] = useState<Decisions | null>(null)

  const resetGame = useCallback(() => {
    setState(INITIAL_STATE)
    setLastDecisions(null)
  }, [])

  const advanceTurn = useCallback((decisions: Decisions) => {
    setState((prev) => {
      // 1. Calculate Costs
      const totalEngineers = prev.engineers + decisions.newEngineers
      const totalSales = prev.sales + decisions.newSales

      // Salary costs with multiplier
      const engineerCost =
        totalEngineers *
        GAME_CONSTANTS.BASE_SALARY_ENGINEER *
        decisions.salaryMultiplier
      const salesCost =
        totalSales *
        GAME_CONSTANTS.BASE_SALARY_SALES *
        decisions.salaryMultiplier
      const operatingCost =
        GAME_CONSTANTS.BASE_OPERATING_COST +
        (totalEngineers + totalSales) * 1000 // Overhead per employee

      const totalExpenses = engineerCost + salesCost + operatingCost

      // 2. Calculate Product Quality
      // Quality improves with more engineers, but diminishes with rapid hiring or low salaries
      const hiringPenalty =
        decisions.newEngineers + decisions.newSales >
        (prev.engineers + prev.sales) * 0.5
          ? 0.9
          : 1
      const salaryFactor =
        decisions.salaryMultiplier > 1
          ? 1 + (decisions.salaryMultiplier - 1) * 0.5
          : decisions.salaryMultiplier

      // Base quality improvement from engineering team size
      const qualityGain =
        Math.log(totalEngineers) * 2 * salaryFactor * hiringPenalty
      let newQuality = Math.min(
        100,
        Math.max(0, prev.productQuality + qualityGain),
      )

      // 3. Calculate Revenue
      // Demand is based on quality and price
      const priceFactor = Math.max(0.1, 200 / decisions.unitPrice) // Lower price = higher demand
      const qualityFactor = newQuality / 50
      const marketDemand =
        GAME_CONSTANTS.MARKET_DEMAND_BASE *
        priceFactor *
        qualityFactor *
        (1 + prev.turn * 0.05) // Market grows over time

      // Sales capacity
      const salesCapacity =
        totalSales * GAME_CONSTANTS.SALES_CAPACITY_PER_REP * salaryFactor

      // Actual sales is min of demand and capacity
      const unitsSold = Math.min(marketDemand, salesCapacity)
      const revenue = unitsSold * decisions.unitPrice

      // 4. Update Cash
      const netIncome = revenue - totalExpenses
      const newCash = prev.cash + netIncome

      // 5. Check Game Over Conditions
      let gameStatus: GameState['gameStatus'] = 'playing'
      if (newCash < 0) {
        gameStatus = 'bankrupt'
      } else if (prev.turn >= GAME_CONSTANTS.MAX_TURNS) {
        gameStatus = 'victory' // Survived 10 years
      }

      const newTurn = prev.turn + 1

      const newState: GameState = {
        turn: newTurn,
        isGameOver: gameStatus !== 'playing',
        gameStatus,
        cash: newCash,
        revenue,
        expenses: totalExpenses,
        netIncome,
        engineers: totalEngineers,
        sales: totalSales,
        productQuality: newQuality,
        history: [
          ...prev.history,
          {
            turn: newTurn,
            cash: newCash,
            revenue,
            netIncome,
            headcount: totalEngineers + totalSales,
          },
        ],
      }

      return newState
    })
    setLastDecisions(decisions)
  }, [])

  return {
    state,
    advanceTurn,
    resetGame,
    lastDecisions,
  }
}
